import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import { useStaffRealtime } from '../../context/StaffRealtimeContext';
import {
  DEFAULT_ITEMS_PER_PAGE,
  getStaffInAppNotifications,
  ITEMS_PER_PAGE_OPTIONS,
} from '../../services/staffInAppNotificationService';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import {
  getStaffNotificationPath,
  getStaffNotificationBody,
  getStaffNotificationTitle,
  getWaitlistAlertFromNotification,
  getAppointmentConfirmationAlertFromNotification,
} from '../../utils/staffNotificationNavigation';
import PushNotificationControl from '../../components/notifications/PushNotificationControl';

const StaffNotificationsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { markRead, markAllRead, unreadCount, openWaitlistAlert, openConfirmationAlert } = useStaffRealtime();

  const [notifications, setNotifications] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadNotifications = useCallback(async (
    nextPageIndex = pageIndex,
    nextItemsPerPage = itemsPerPage,
  ) => {
    if (!storeId) {
      setError(t('staffNotifications.page.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getStaffInAppNotifications(storeId, {
        pageIndex: nextPageIndex,
        itemsPerPage: nextItemsPerPage,
      });
      setNotifications(result.data);
      setTotalItems(result.totalItems);
      setPageIndex(result.pageIndex);
      setItemsPerPage(result.itemsPerPage || nextItemsPerPage);
    } catch (loadError) {
      console.error('Failed to load staff notifications:', loadError);
      setError(loadError?.response?.data?.message || t('staffNotifications.page.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, pageIndex, storeId, t]);

  useEffect(() => {
    loadNotifications(0, itemsPerPage);
    // Initial load / store change only — page/size changes go through handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const pageCount = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const rangeFrom = totalItems === 0 ? 0 : pageIndex * itemsPerPage + 1;
  const rangeTo = Math.min(totalItems, (pageIndex + 1) * itemsPerPage);

  const handlePageChange = (_, newPage) => {
    loadNotifications(newPage, itemsPerPage);
  };

  const handleItemsPerPageChange = (event) => {
    const nextItemsPerPage = Number(event.target.value);
    setItemsPerPage(nextItemsPerPage);
    loadNotifications(0, nextItemsPerPage);
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.isRead) {
      try {
        await markRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) => (
            String(item.id).toLowerCase() === String(notification.id).toLowerCase()
              ? { ...item, isRead: true }
              : item
          ))
        );
      } catch (markError) {
        console.error('Failed to mark notification as read', markError);
      }
    }

    const target = getStaffNotificationPath(notification);
    if (target) {
      navigate(target);
    }
    const waitlistAlert = getWaitlistAlertFromNotification(notification);
    if (waitlistAlert) {
      openWaitlistAlert?.(waitlistAlert);
    }
    const confirmationAlert = getAppointmentConfirmationAlertFromNotification(notification);
    if (confirmationAlert) {
      openConfirmationAlert?.(confirmationAlert);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } catch (markError) {
      console.error('Failed to mark all notifications as read', markError);
    }
  };

  if (loading && notifications.length === 0 && !error) {
    return (
      <ManagementPageLayout
        pageTitle={t('staffNotifications.page.title')}
        pageSubtitle={t('staffNotifications.page.subtitle')}
      >
        <ManagementPageSkeleton />
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout
      pageTitle={t('staffNotifications.page.title')}
      pageSubtitle={t('staffNotifications.page.subtitle')}
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <ManagementPageCard sx={{ mb: 2 }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <PushNotificationControl audience="staff" />
        </Box>
      </ManagementPageCard>

      <ManagementPageCard>
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {unreadCount > 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
                mb: { xs: 2.5, md: 3 },
              }}
            >
              <Button
                onClick={handleMarkAllRead}
                fullWidth={isMobile}
                sx={{ color: BRAND.accentText, textTransform: 'none', fontWeight: 700 }}
              >
                {t('staffNotifications.markAllRead')}
              </Button>
            </Box>
          ) : null}

          {notifications.length === 0 && !loading ? (
            <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
              <NotificationsIcon sx={{ fontSize: 48, color: BRAND.accentText, mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('staffNotifications.empty')}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5, opacity: loading ? 0.6 : 1 }}>
                {notifications.map((notification) => (
                  <Box
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      border: `1px solid ${BRAND.line}`,
                      borderRadius: BRAND_RADIUS.button,
                      p: 2,
                      backgroundColor: notification.isRead ? BRAND.surface : brandAlpha('accent', 0.08),
                      cursor: getStaffNotificationPath(notification) || !notification.isRead ? 'pointer' : 'default',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: notification.isRead ? 600 : 700, mb: 0.5 }}>
                      {getStaffNotificationTitle(notification, t)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                      {getStaffNotificationBody(notification, t)}
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {formatUtcDateTime(notification.createdAtUtc)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <TableContainer sx={{ display: { xs: 'none', md: 'block' }, opacity: loading ? 0.6 : 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('staffNotifications.page.table.title')}</TableCell>
                      <TableCell>{t('staffNotifications.page.table.message')}</TableCell>
                      <TableCell>{t('staffNotifications.page.table.dateTime')}</TableCell>
                      <TableCell>{t('staffNotifications.page.table.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow
                        key={notification.id}
                        hover
                        onClick={() => handleNotificationClick(notification)}
                        sx={{
                          cursor: getStaffNotificationPath(notification) || !notification.isRead ? 'pointer' : 'default',
                          bgcolor: notification.isRead ? 'transparent' : brandAlpha('accent', 0.06),
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: notification.isRead ? 500 : 700 }}>
                            {getStaffNotificationTitle(notification, t)}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStaffNotificationBody(notification, t)}</TableCell>
                        <TableCell>{formatUtcDateTime(notification.createdAtUtc)}</TableCell>
                        <TableCell>
                          {notification.isRead
                            ? t('staffNotifications.page.status.read')
                            : t('staffNotifications.page.status.unread')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'space-between' },
                  gap: 1.5,
                  borderTop: `1px solid ${BRAND.line}`,
                  mt: 2,
                  pt: 1.5,
                  px: { xs: 0, sm: 1 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: { xs: 1.5, sm: 2 },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text, whiteSpace: 'nowrap' }}>
                      {t('common.rowsPerPage')}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 72 }}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        disabled={loading}
                        MenuProps={{ disableScrollLock: true }}
                        sx={{
                          height: 36,
                          fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.line,
                            borderWidth: '2px',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.accent,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: BRAND.accent,
                          },
                        }}
                      >
                        {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.text }}>
                    {`${rangeFrom}-${rangeTo} ${t('common.of')} ${totalItems}`}
                  </Typography>
                </Box>
                <Pagination
                  count={pageCount}
                  page={pageIndex + 1}
                  onChange={(_, page) => handlePageChange(null, page - 1)}
                  disabled={loading}
                  showFirstButton={!isMobile}
                  showLastButton={!isMobile}
                  siblingCount={isMobile ? 0 : 1}
                  boundaryCount={1}
                  size={isMobile ? 'small' : 'medium'}
                  color="primary"
                  sx={{
                    order: { xs: 1, sm: 2 },
                    '& .MuiPaginationItem-root': {
                      fontWeight: 600,
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${BRAND.accent} !important`,
                      color: BRAND.onAccent,
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </ManagementPageCard>
    </ManagementPageLayout>
  );
};

export default StaffNotificationsPage;
